import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { TranslocoService } from '@ngneat/transloco';
import {
  UpdateUserSettingsDto,
  UserSettingsDto,
} from '@splitsecondconnect/ultramarine-shared';
import { UserSettingsService } from '../services/user-settings.service';
import { SettingsStore } from './settings.store';
import { AlertsService } from '../services/alerts.service';
import { httpErrorMessage } from '../helpers/http-error-message';
import { catchError, finalize, of, tap } from 'rxjs';

interface UserSettingsState {
  isUserSettingsLoading: boolean;
  isUserSettingsSaving: boolean;
  canViewUserSettings: boolean;
  userSettings?: UserSettingsDto;
}

const initialState: UserSettingsState = {
  isUserSettingsLoading: false,
  isUserSettingsSaving: false,
  canViewUserSettings: true,
  userSettings: undefined,
};

export const UserSettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      userSettingsService = inject(UserSettingsService),
      tenantSettingsStore = inject(SettingsStore),
      transloco = inject(TranslocoService),
      document = inject(DOCUMENT),
      alerts = inject(AlertsService),
    ) => {
      const syncUiLanguage = () => {
        const languageId = store.userSettings?.()?.languageId;
        const languages = tenantSettingsStore.languages();
        const isoCode = languages.find((lang) => lang.id === languageId)?.isoCode;
        if (!isoCode) {
          return;
        }
        transloco.setActiveLang(isoCode);
        document.documentElement.lang = isoCode;
      };

      return {
        load() {
          patchState(store, { isUserSettingsLoading: true });
          return userSettingsService.getSettings().pipe(
            tap((res) => {
              patchState(store, {
                userSettings: res.data,
                canViewUserSettings: true,
              });
              syncUiLanguage();
            }),
            catchError((error: unknown) => {
              if (error instanceof HttpErrorResponse && error.status === 403) {
                patchState(store, {
                  canViewUserSettings: false,
                  userSettings: undefined,
                });
              }
              return of(null);
            }),
            finalize(() =>
              patchState(store, { isUserSettingsLoading: false }),
            ),
          );
        },
        updateUserSettings(dto: UpdateUserSettingsDto) {
          patchState(store, { isUserSettingsSaving: true });
          return userSettingsService.updateSettings(dto).pipe(
            tap((res) => {
              patchState(store, { userSettings: res.data });
              syncUiLanguage();
            }),
            catchError((error) => {
              void alerts.error(
                httpErrorMessage(
                  error,
                  transloco.translate('alerts|something-went-wrong'),
                ),
              );
              return of(null);
            }),
            finalize(() =>
              patchState(store, { isUserSettingsSaving: false }),
            ),
          );
        },
      };
    },
  ),
);
